// 換頁銜接（2026-09-25 三站動態設計 Phase A；規格 SSOT＝vault《三站動態設計：從硬切到銜接》§3–§5）
//
// 剪接語法：舊頁 200ms 淡出、新頁晚 60ms 以 480ms 淡入＋上浮 12px；返回下沉 8px、時長 ×0.8；平行導覽只淡。
// 不再過黑：舊的退、新的進是重疊的（View Transitions），頁首 site-header 不參與。
//
// 時序（研究報告 §2.3「先載完再轉」）：
//   beforeResolve（lazy chunk 與內文都已載好）→ startViewTransition 拍舊畫面 → update 裡才放行導航
//   → 新頁同步掛載 → router.scrollBehavior 捲到定位 → releaseViewTransition() → 瀏覽器拍新畫面、開始動畫。
// 凍結期只剩 DOM 替換＋捲動（實測數十毫秒）；800ms 保險逾時直接放行。新導航會 skipTransition() 打斷舊轉場。
// 不支援 View Transitions 或開減少動態：不走轉場，新頁掛載時只做進場淡入（舊頁立即移除，不過黑）。
import { holdSmoothScroll } from './smooth-scroll'

const BOT = /bot|crawler|spider|headless|prerender/i
export const isBot = () => typeof navigator !== 'undefined' && BOT.test(navigator.userAgent)
export const prefersReducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
export const supportsViewTransitions = () => typeof document !== 'undefined' && typeof document.startViewTransition === 'function'

let current = null        // 進行中的轉場 { vt, release, released, to, from, direction }
let enterMode = null      // 非 VT 路徑的進場方式：'fade' | 'reduced'，由新頁掛載時取用
let lastPosition = null   // vue-router 寫在 history.state 的 position，用來分辨返回
let appMounted = false    // 掛載前的導航（首次語系 replace）不做轉場
const hooks = new Set()   // Phase B：共享元素（作品浮出）在這裡掛 before／ready／after

export function markAppMounted() { appMounted = true }
export function addTransitionHook(hook) { hooks.add(hook); return () => hooks.delete(hook) }
export function viewTransitionPending() { return !!current && !current.released }
export function currentTransition() { return current }
export function consumeEnterMode() { const mode = enterMode; enterMode = null; return mode }

const stripLocale = path => path.replace(/^\/zh(?=\/|$)/, '').replace(/\/+$/, '')
const depth = path => stripLocale(path).split('/').filter(Boolean).length
export function navDirection(to, from) {
  const position = typeof history !== 'undefined' ? history.state?.position : undefined
  // popstate 時 history.state 已經換成目的地；一般 push 時仍是出發頁（position 相等）
  if (typeof position === 'number' && typeof lastPosition === 'number' && position !== lastPosition)
    return position < lastPosition ? 'back' : 'forward'
  const target = stripLocale(to.path), origin = stripLocale(from.path)
  if (!target) return 'lateral'                              // 回首頁（logo）＝平行
  if (depth(to.path) > depth(from.path)) return 'forward'    // 往下一層：列表 → 作品、Thinking → 文章
  if (origin.startsWith(target + '/')) return 'back'         // 回上一層：「← 返回作品檔案」
  return 'lateral'
}

function runHooks(name, ...args) {
  for (const hook of hooks) {
    try { hook[name]?.(...args) } catch (error) { console.warn('[view-transition] hook', name, error) }
  }
}

export function releaseViewTransition() {
  const entry = current
  if (!entry || entry.released) return
  entry.released = true
  clearTimeout(entry.safety)
  runHooks('ready', entry)
  entry.release()
}

export function installViewTransitions(router) {
  if (typeof window === 'undefined') return
  lastPosition = history.state?.position
  router.beforeResolve((to, from) => {
    if (!appMounted || !from.matched.length || to.path === from.path || isBot()) return
    const direction = navDirection(to, from)
    if (!supportsViewTransitions() || prefersReducedMotion()) {
      enterMode = prefersReducedMotion() ? 'reduced' : 'fade'
      return
    }
    if (current) {
      // 新導航打斷舊轉場：先放掉舊的 update（否則它的快照永遠等不到新畫面），再跳到結尾
      if (!current.released) { current.released = true; clearTimeout(current.safety); current.release() }
      current.vt?.skipTransition()
    }
    const root = document.documentElement
    return new Promise(resolveGuard => {
      let finishUpdate
      const updateDone = new Promise(resolve => (finishUpdate = resolve))
      const entry = { to, from, direction, released: false, release: () => finishUpdate() }
      root.dataset.nav = direction
      holdSmoothScroll(true)
      runHooks('before', entry)
      let vt
      try {
        vt = document.startViewTransition(() => { resolveGuard(); return updateDone })
      } catch (error) {
        holdSmoothScroll(false)
        delete root.dataset.nav
        enterMode = 'fade'
        resolveGuard()
        return
      }
      entry.vt = vt
      entry.safety = setTimeout(releaseViewTransition, 800)
      current = entry
      vt.finished.catch(() => {}).finally(() => {
        runHooks('after', entry)
        if (current !== entry) return
        current = null
        delete root.dataset.nav
        holdSmoothScroll(false)
      })
    })
  })
  router.afterEach((to, from, failure) => {
    lastPosition = history.state?.position
    // 被取消或重複的導航不會走到 scrollBehavior：馬上放行，畫面不能卡在舊快照
    if (failure) releaseViewTransition()
  })
}
